import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Modal,
  Form,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Tooltip,
  Switch,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ToolOutlined,
} from '@ant-design/icons';

const { TextArea } = Input;

const StyleManagement: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [form] = Form.useForm();

  // 模拟风格数据
  const styleData = [
    {
      id: 1,
      styleName: '官方',
      styleCode: 'OFFICIAL',
      description: '正式、严谨的表达风格，适合重要通知和规则说明',
      status: 'enabled',
      createdAt: '2024-01-01 10:00:00',
      updatedAt: '2024-10-20 14:30:00',
    },
    {
      id: 2,
      styleName: '热情',
      styleCode: 'ENTHUSIASTIC',
      description: '热情洋溢、积极向上的表达风格，适合欢迎和激励',
      status: 'enabled',
      createdAt: '2024-01-01 10:00:00',
      updatedAt: '2024-10-20 14:30:00',
    },
    {
      id: 3,
      styleName: '友好',
      styleCode: 'FRIENDLY',
      description: '亲切友好、平易近人的表达风格，适合日常交流',
      status: 'enabled',
      createdAt: '2024-01-01 10:00:00',
      updatedAt: '2024-10-20 14:30:00',
    },
    {
      id: 4,
      styleName: '搞笑',
      styleCode: 'FUNNY',
      description: '幽默风趣、轻松愉快的表达风格，适合活跃气氛',
      status: 'enabled',
      createdAt: '2024-01-01 10:00:00',
      updatedAt: '2024-10-20 14:30:00',
    },
    {
      id: 5,
      styleName: '吐槽',
      styleCode: 'COMPLAIN',
      description: '诙谐幽默的自嘲和吐槽风格，适合娱乐场景',
      status: 'enabled',
      createdAt: '2024-01-01 10:00:00',
      updatedAt: '2024-10-20 14:30:00',
    },
    {
      id: 6,
      styleName: '擦边',
      styleCode: 'EDGY',
      description: '带有一丝敏感话题的表达风格，需谨慎使用',
      status: 'enabled',
      createdAt: '2024-01-01 10:00:00',
      updatedAt: '2024-10-20 14:30:00',
    },
  ];

  const handleAddStyle = () => {
    setSelectedRecord(null);
    form.resetFields();
    setEditModalVisible(true);
  };

  const handleEditStyle = (record: any) => {
    setSelectedRecord(record);
    form.setFieldsValue({
      ...record,
      status: record.status === 'enabled',
    });
    setEditModalVisible(true);
  };

  const handleViewDetail = (record: any) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  const handleSaveStyle = async (values: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (selectedRecord) {
        message.success('风格更新成功！');
      } else {
        message.success('风格创建成功！');
      }
      setEditModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('保存失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStyle = (record: any) => {
    message.loading('正在删除风格...', 1);
    setTimeout(() => {
      message.success(`已删除风格 ${record.styleName}`);
    }, 1000);
  };

  const handleToggleStatus = (record: any) => {
    const newStatus = record.status === 'enabled' ? 'disabled' : 'enabled';
    message.loading('正在更新状态...', 1);
    setTimeout(() => {
      message.success(`风格 ${record.styleName} 已${newStatus === 'enabled' ? '启用' : '禁用'}`);
    }, 1000);
  };

  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的风格');
      return;
    }
    message.loading('正在批量删除...', 1);
    setTimeout(() => {
      message.success(`已删除 ${selectedRowKeys.length} 个风格`);
      setSelectedRowKeys([]);
    }, 1000);
  };

  const getStatusColor = (status: string) => {
    return status === 'enabled' ? 'green' : 'red';
  };

  const getStatusName = (status: string) => {
    return status === 'enabled' ? '启用' : '禁用';
  };

  const columns = [
    {
      title: '风格信息',
      key: 'styleInfo',
      width: 200,
      render: (_: any, record: any) => (
        <Space>
          <div>
            <div style={{ fontWeight: 'bold', fontSize: 16 }}>
              {record.styleName}
            </div>
            <div style={{ fontSize: 12, color: '#999' }}>{record.styleCode}</div>
          </div>
        </Space>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: 300,
    },
    {
      title: '状态',
      key: 'status',
      width: 100,
      render: (_: any, record: any) => (
        <Tag color={getStatusColor(record.status)}>
          {getStatusName(record.status)}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 160,
    },
    {
      title: '操作',
      key: 'action',
      width: 250,
      fixed: 'right' as const,
      render: (_: any, record: any) => (
        <Space>
          <Tooltip title="查看详情">
            <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
              详情
            </Button>
          </Tooltip>
          <Tooltip title="编辑风格">
            <Button type="link" icon={<EditOutlined />} onClick={() => handleEditStyle(record)}>
              编辑
            </Button>
          </Tooltip>
          <Tooltip title={record.status === 'enabled' ? '禁用风格' : '启用风格'}>
            <Button 
              type="link" 
              onClick={() => handleToggleStatus(record)}
            >
              {record.status === 'enabled' ? '禁用' : '启用'}
            </Button>
          </Tooltip>
          <Popconfirm
            title="确定要删除这个风格吗？"
            description="删除后无法恢复，请谨慎操作"
            onConfirm={() => handleDeleteStyle(record)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除风格">
              <Button type="link" icon={<DeleteOutlined />} danger>
                删除
              </Button>
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filteredData = styleData.filter(item => {
    const matchesSearch = !searchText || 
      item.styleName.toLowerCase().includes(searchText.toLowerCase()) ||
      item.styleCode.toLowerCase().includes(searchText.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchText.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || item.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <h2>风格管理</h2>
        <p style={{ color: '#666' }}>管理智能体的表达风格，定义不同的输出方式</p>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic 
              title="总风格数" 
              value={styleData.length} 
              prefix={<ToolOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic 
              title="启用风格" 
              value={styleData.filter(item => item.status === 'enabled').length}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic 
              title="禁用风格" 
              value={styleData.filter(item => item.status === 'disabled').length}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic 
              title="本月新增" 
              value={0}
              prefix={<PlusOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="风格列表"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => message.info('数据已刷新')}
            >
              刷新
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddStyle}
            >
              新增风格
            </Button>
          </Space>
        }
      >
        {/* 搜索和筛选 */}
        <div style={{ marginBottom: 16 }}>
          <Space wrap>
            <Input
              placeholder="搜索风格名称、编码或描述"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
            />
            <Space>
              <Button
                type={filterStatus === 'all' ? 'primary' : 'default'}
                onClick={() => setFilterStatus('all')}
              >
                全部
              </Button>
              <Button
                type={filterStatus === 'enabled' ? 'primary' : 'default'}
                onClick={() => setFilterStatus('enabled')}
              >
                启用
              </Button>
              <Button
                type={filterStatus === 'disabled' ? 'primary' : 'default'}
                onClick={() => setFilterStatus('disabled')}
              >
                禁用
              </Button>
            </Space>
            {selectedRowKeys.length > 0 && (
              <Popconfirm
                title={`确定要删除选中的 ${selectedRowKeys.length} 个风格吗？`}
                onConfirm={handleBatchDelete}
                okText="确定"
                cancelText="取消"
              >
                <Button danger icon={<DeleteOutlined />}>
                  批量删除
                </Button>
              </Popconfirm>
            )}
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          rowSelection={rowSelection}
          pagination={{ pageSize: 20 }}
          loading={loading}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* 编辑风格模态框 */}
      <Modal
        title={selectedRecord ? '编辑风格' : '新增风格'}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        width={600}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveStyle}
          initialValues={{
            status: true,
          }}
        >
          <Form.Item
            name="styleName"
            label="风格名称"
            rules={[{ required: true, message: '请输入风格名称' }]}
          >
            <Input placeholder="如：官方、热情、友好等" />
          </Form.Item>

          <Form.Item
            name="styleCode"
            label="风格编码"
            rules={[
              { required: true, message: '请输入风格编码' },
              { pattern: /^[A-Z_]+$/, message: '编码只能使用大写字母和下划线' }
            ]}
          >
            <Input placeholder="如：OFFICIAL、FRIENDLY等" />
          </Form.Item>

          <Form.Item
            name="description"
            label="风格描述"
            rules={[{ required: true, message: '请输入风格描述' }]}
          >
            <TextArea
              rows={4}
              placeholder="详细描述该风格的表达特点和适用场景"
            />
          </Form.Item>

          <Form.Item
            name="status"
            label="启用状态"
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setEditModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {selectedRecord ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情模态框 */}
      <Modal
        title="风格详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        width={600}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
        ]}
      >
        {selectedRecord && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ marginBottom: 8 }}>基本信息</h3>
              <p><strong>风格名称：</strong>{selectedRecord.styleName}</p>
              <p><strong>风格编码：</strong><code>{selectedRecord.styleCode}</code></p>
              <p><strong>状态：</strong>
                <Tag color={getStatusColor(selectedRecord.status)} style={{ marginLeft: 8 }}>
                  {getStatusName(selectedRecord.status)}
                </Tag>
              </p>
            </div>
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ marginBottom: 8 }}>风格描述</h3>
              <p>{selectedRecord.description}</p>
            </div>
            <div>
              <h3 style={{ marginBottom: 8 }}>时间信息</h3>
              <p><strong>创建时间：</strong>{selectedRecord.createdAt}</p>
              <p><strong>更新时间：</strong>{selectedRecord.updatedAt}</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StyleManagement;

